import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiDownload, FiHeart, FiHome, FiChevronDown, FiChevronUp, FiCalendar } from 'react-icons/fi';
import styles from './DonationConfirmation.module.css';

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function DonationConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  
  // Get donation data from location state or URL params (when Stripe redirects)
  let donationData = location.state?.donationData;
  
  // If no state data, try to get from URL params (Stripe redirect)
  if (!donationData) {
    const searchParams = new URLSearchParams(location.search);
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    
    if (paymentIntent || paymentIntentClientSecret) {
      // Redirect to donation form if we don't have full data
      // This shouldn't normally happen, but handle gracefully
      setTimeout(() => {
        navigate('/donation');
      }, 2000);
      
      return (
        <div className={styles.container}>
          <div className={`${styles.card} ${styles.hero}`}>
            <p className={styles.subtitle}>Processing your donation confirmation...</p>
          </div>
        </div>
      );
    }
  }
  
  if (!donationData) {
    return (
      <div className={styles.container}>
        <div className={`${styles.card} ${styles.hero}`}>
          <p className={styles.subtitle}>No donation information found. Please try again.</p>
          <div className={styles.buttonGroup}>
            <button className={styles.actionButton} style={{background: "var(--brand)", color: "#fff"}} onClick={() => navigate('/donation')}>
              <FiHeart size={16} />
              Make a Donation
            </button>
            <button className={styles.actionButton} onClick={() => navigate('/dashboard')}>
              <FiHome size={16} />
              Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    donationType, // 'one-time' or 'monthly'
    oneTimeAmount,
    monthlyAmount,
    numberOfPayments,
    isForever,
    user,
    receiptUrl,
    paymentIntentId,
    subscriptionScheduleId,
    subscriptionId
  } = donationData;

  // Calculate start and end dates for monthly donations
  const getDonationDates = () => {
    if (donationType !== 'monthly' || isForever || numberOfPayments === 'forever') {
      return null;
    }
    
    const startDate = new Date();
    const numPayments = parseInt(numberOfPayments) || 0;
    
    // End date is start date + (numberOfPayments - 1) months
    // For example, if starting today and 5 payments, end date is 4 months from now
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + (numPayments - 1));
    
    // Generate payment schedule (same day each month)
    const paymentDates = [];
    for (let i = 0; i < numPayments; i++) {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(paymentDate.getMonth() + i);
      paymentDates.push(paymentDate);
    }
    
    return {
      startDate,
      endDate,
      paymentDates
    };
  };

  const donationDates = getDonationDates();

  const handleDownloadReceipt = () => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  const getDonationTypeText = () => {
    if (donationType === 'one-time') {
      return 'One-Time Donation';
    } else {
      return 'Monthly Recurring Donation';
    }
  };

  const getThankYouMessage = () => {
    const name = user?.firstName || user?.first_name || 'Friend';
    return `Jazakamullahu Khayran, ${name} — May Allah SWT reward you abundantly for your generosity. Your contribution truly makes a difference.`;
  };

  return (
    <div className={styles.container}>
      <div className={`${styles.card} ${styles.hero}`}>
        <div className={styles.heroIcon}>
          <FiCheckCircle size={40} color="white" />
        </div>

        <h1 className={styles.headline}>Donation Confirmed!</h1>

        <p className={styles.subtitle}>
          {getThankYouMessage()}
        </p>

        <div className={styles.card}>
          <h2 className={styles.summaryHeader}>
            <FiHeart size={20} />
            Donation Details
          </h2>
          
          <div className={styles.lineRow}>
            <span className={styles.lineLabel}>Donation Type:</span>
            <span className={styles.lineValue}>{getDonationTypeText()}</span>
          </div>
          
          {donationType === 'one-time' && oneTimeAmount && (
            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>Amount:</span>
              <span className={styles.lineValue}>{formatCurrency(oneTimeAmount)}</span>
            </div>
          )}
          
          {donationType === 'monthly' && monthlyAmount && (numberOfPayments || isForever) && (
            <>
              <div className={styles.lineRow}>
                <span className={styles.lineLabel}>Monthly Amount:</span>
                <span className={styles.lineValue}>{formatCurrency(monthlyAmount)}</span>
              </div>
              <div className={styles.lineRow}>
                <span className={styles.lineLabel}>Duration:</span>
                <span className={styles.lineValue}>{isForever || numberOfPayments === 'forever' ? 'Unlimited' : `${numberOfPayments} months`}</span>
              </div>
              {!(isForever || numberOfPayments === 'forever') && (
                <div className={styles.lineRow}>
                  <span className={styles.lineLabel}>Total Commitment:</span>
                  <span className={styles.lineValue}>{formatCurrency(parseFloat(monthlyAmount) * parseInt(numberOfPayments))}</span>
                </div>
              )}
            </>
          )}
          
          <div className={styles.lineRow}>
            <span className={styles.lineLabel}>{donationType === 'one-time' ? 'Date' : 'Donation Start Date'}:</span>
            <span className={styles.lineValue}>{new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span>
          </div>
          
          {donationType === 'monthly' && donationDates && !isForever && numberOfPayments !== 'forever' && (
            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>Donation End Date:</span>
              <span className={styles.lineValue}>{donationDates.endDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          )}
          
          {user && (
            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>Donor Name:</span>
              <span className={styles.lineValue}>{user.firstName || user.first_name} {user.lastName || user.last_name}</span>
            </div>
          )}
          
          {paymentIntentId && (
            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>Transaction ID:</span>
              <span className={styles.lineValue} style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {paymentIntentId}
              </span>
            </div>
          )}

          {subscriptionScheduleId && (
            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>Subscription Schedule ID:</span>
              <span className={styles.lineValue} style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {subscriptionScheduleId}
              </span>
            </div>
          )}

          {subscriptionId && (
            <div className={styles.lineRow}>
              <span className={styles.lineLabel}>Subscription ID:</span>
              <span className={styles.lineValue} style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                {subscriptionId}
              </span>
            </div>
          )}
          
          {donationType === 'monthly' && donationDates && !isForever && numberOfPayments !== 'forever' && (
            <div style={{marginTop: "0.5rem"}}>
              <button className={styles.actionButton} style={{background: "none", border: "none", justifyContent: "space-between"}} onClick={() => setScheduleExpanded(!scheduleExpanded)}>
                <span>
                  <FiCalendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Payment Schedule
                </span>
                {scheduleExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </button>
              
              {scheduleExpanded && (
                <div style={{marginTop: "0.75rem"}}>
                  {donationDates.paymentDates.map((date, index) => (
                    <div className={styles.lineRow} key={index}>
                      <span style={{color: "#171717", fontWeight: 500}}>
                        {date.toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                      <span style={{color: "#171717", fontWeight: 600}}>
                        {formatCurrency(monthlyAmount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {user?.email && (
          <div className={styles.emailNotice}>
            A confirmation email has been sent to <strong>{user.email}</strong> with your donation details{receiptUrl ? ' and receipt' : ''}.
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button className={styles.actionButton} style={{background: "var(--brand)", color: "#fff"}} onClick={() => navigate('/dashboard')}>
            <FiHome size={16} />
            Go to Dashboard
          </button>

          <button className={styles.actionButton} onClick={() => navigate('/donation')}>
            <FiHeart size={16} />
            Make Another Donation
          </button>

          {receiptUrl && (
            <button className={styles.actionButton} style={{background: "var(--brand)", color: "#fff"}} onClick={handleDownloadReceipt}>
              <FiDownload size={16} />
              Download Receipt
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DonationConfirmation;

